/**
 * Utility functions to replace lodash
 * These are lightweight native JavaScript implementations
 */

type Path = string | string[]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function hasEmptyRichTextItem(value: unknown): boolean {
  return isRecord(value) && 'text' in value && value.text === ''
}

/** Compare sort keys returned by get(); narrows unknown before using `<`. */
function compareSortValues(aVal: unknown, bVal: unknown): number {
  if (aVal == null) return 1
  if (bVal == null) return -1

  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return aVal < bVal ? -1 : 1
  }

  if (typeof aVal === 'string' && typeof bVal === 'string') {
    return aVal < bVal ? -1 : 1
  }

  if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
    return Number(aVal) - Number(bVal)
  }

  if (typeof aVal === 'bigint' && typeof bVal === 'bigint') {
    return aVal < bVal ? -1 : 1
  }

  const aText = String(aVal)
  const bText = String(bVal)
  return aText < bText ? -1 : aText > bText ? 1 : 0
}

/**
 * Get nested property value from object
 * Replaces lodash.get
 * @example get({ a: { b: { c: 1 } } }, 'a.b.c') // returns 1
 * @example get({ a: { b: null } }, 'a.b', 'default') // returns null (not 'default')
 */
export function get(obj: unknown, path: Path, defaultValue?: undefined): unknown
export function get<T>(obj: unknown, path: Path, defaultValue: T): T
export function get(obj: unknown, path: Path, defaultValue?: unknown): unknown {
  const keys = Array.isArray(path) ? path : path.split('.')
  let result: unknown = obj
  for (const key of keys) {
    if (!isRecord(result)) return defaultValue
    result = result[key]
    if (result === undefined) return defaultValue
  }
  return result === undefined ? defaultValue : result
}

/**
 * Sort array by key with order
 * Replaces lodash.orderBy
 * @example orderBy(users, 'data.age', 'desc')
 * @example orderBy(users, ['name', 'age'], ['asc', 'desc'])
 */
export const orderBy = <T>(
  arr: T[] | unknown,
  keys: string | string[],
  orders?: 'asc' | 'desc' | ('asc' | 'desc')[],
): T[] => {
  // Safety check: return empty array if input is not an array
  if (!Array.isArray(arr)) {
    return [] as T[]
  }

  const keyArray = Array.isArray(keys) ? keys : [keys]
  const orderArray = orders
    ? Array.isArray(orders)
      ? orders
      : keyArray.map(() => orders)
    : keyArray.map(() => 'asc' as const)

  return [...arr].sort((a, b) => {
    for (let i = 0; i < keyArray.length; i++) {
      const key = keyArray[i]
      const order = orderArray[i]
      const aVal = get(a, key)
      const bVal = get(b, key)

      if (aVal !== bVal) {
        const comparison = compareSortValues(aVal, bVal)
        return order === 'asc' ? comparison : -comparison
      }
    }
    return 0
  })
}

/**
 * Find index of element matching predicate
 * Replaces lodash.findIndex
 * @example findIndex(users, ['data.id', 123])
 */
export const findIndex = <T>(arr: T[], predicate: Partial<T> | ((item: T) => boolean) | [string, unknown]): number => {
  if (typeof predicate === 'function') {
    return arr.findIndex(predicate)
  }
  // Handle array format like ['data.article_position', value]
  if (Array.isArray(predicate)) {
    const [path, value] = predicate
    return arr.findIndex((item) => get(item, path) === value)
  }
  return arr.findIndex((item) => {
    const record = item as Record<string, unknown>
    return Object.keys(predicate).every((key) => record[key] === predicate[key as keyof T])
  })
}

/**
 * Remove duplicate objects from array by key
 * Replaces lodash.uniqBy
 * @example uniqBy(users, 'uid')
 */
export const uniqBy = <T>(arr: T[], key: keyof T | ((item: T) => unknown)): T[] => {
  const seen = new Map<unknown, boolean>()
  return arr.filter((item) => {
    const val = typeof key === 'function' ? key(item) : item[key]
    if (seen.has(val)) return false
    seen.set(val, true)
    return true
  })
}

/**
 * Check if value is empty
 * Replaces lodash.isEmpty
 * Handles objects, arrays, strings, and Prismic fields
 */
export const isEmpty = (value: unknown): boolean => {
  if (value == null) return true

  // Handle arrays
  if (Array.isArray(value)) {
    return value.length === 0 || (value.length === 1 && hasEmptyRichTextItem(value[0]))
  }

  // Handle Prismic image fields
  if (isRecord(value) && 'url' in value) {
    return !value.url
  }

  // Handle objects
  if (typeof value === 'object') {
    return Object.keys(value).length === 0
  }

  // Handle strings
  if (typeof value === 'string') {
    return value.trim().length === 0
  }

  return false
}

/**
 * Flatten array by one level
 * Replaces lodash.flatten
 * @example flatten([[1, 2], [3, 4]]) // returns [1, 2, 3, 4]
 */
export const flatten = <T>(arr: T[][]): T[] => {
  return arr.flat()
}

/**
 * Get unique values from array
 * Replaces lodash.uniq
 * @example uniq([1, 2, 2, 3]) // returns [1, 2, 3]
 */
export const uniq = <T>(arr: T[]): T[] => {
  return Array.from(new Set(arr))
}

/**
 * Debounce function - delays execution until after wait time has passed
 * Replaces lodash.debounce
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @param immediate - If true, trigger on leading edge instead of trailing
 * @returns A debounced version of the function
 *
 * @example
 * ```ts
 * const debouncedSearch = debounce((query: string) => {
 *   console.log('Searching for:', query)
 * }, 300)
 *
 * debouncedSearch('hello') // Will execute after 300ms of no calls
 * debouncedSearch('world') // Cancels previous call, will execute after 300ms
 * ```
 */
export function debounce<Args extends unknown[], R>(
  func: (...args: Args) => R,
  wait: number,
  immediate = false,
): (...args: Args) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return function debounced(this: unknown, ...args: Args) {
    const callNow = immediate && !timeoutId

    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      timeoutId = null
      if (!immediate) {
        func.apply(this, args)
      }
    }, wait)

    if (callNow) {
      func.apply(this, args)
    }
  }
}
